namespace Hooks {
    export type Callback = () => unknown;
    export type TestCondition<T> = (context: T) => boolean;
    export interface Options<T> {
        persist: boolean;
        condition: TestCondition<T>;
    }
    export interface CallbackConfig<T> {
        options: Options<T>;
        callback: Callback;
    }
    export type CallbackMap<TMap> = {
        [K in keyof TMap]?: Array<CallbackConfig<TMap[K]>>;
    };

    export type ErrorHandler = (msg: Error | ErrorEvent | string) => unknown;
}

class Hooks<EventsMap> {
    public callbacks: Hooks.CallbackMap<EventsMap>;
    public errorHandler: Hooks.ErrorHandler;

    constructor({
        errorHandler = console.error,
    }: {
        errorHandler?: Hooks.ErrorHandler;
    } = {}) {
        this.callbacks = {};
        this.errorHandler = errorHandler;
    }

    private privateCallbacksOf<K extends keyof EventsMap>(event: K) {
        const { callbacks } = this;
        const callbackItem = callbacks[event];
        if (!callbackItem) {
            callbacks[event] = [];
            return callbacks[event]!;
        }
        return callbackItem;
    }

    public addHook<K extends keyof EventsMap>(
        event: K,
        callback: Hooks.Callback,
        options?: Partial<Hooks.Options<EventsMap[K]>>,
    ) {
        const clbks = this.privateCallbacksOf(event);
        clbks.push({
            options: {
                persist: false,
                condition: () => true,
                ...options,
            },
            callback,
        });
    }

    public fireHooks = <K extends keyof EventsMap>(
        event: K,
        context: EventsMap[K],
    ): Promise<void> => {
        const clbks = this.privateCallbacksOf(event);
        const hooks = clbks.filter(hookItem =>
            hookItem.options.condition(context),
        );
        return Promise.all(
            hooks.map(hook => new Promise(resolve => resolve(hook.callback()))),
        )
            .then(() => this.cleanHooks(event))
            .catch(err => {
                this.errorHandler(err);
            });
    };

    public cleanHooks(event: keyof EventsMap) {
        const clbks = this.privateCallbacksOf(event);
        this.callbacks[event] = clbks.filter(
            hookItem => hookItem.options.persist,
        );
    }
}

export { Hooks };
